import { Schema } from "effect";

const CustomEmailDnsRecordType = Schema.Literal("TXT", "CNAME", "MX");

export const CustomEmailDnsRecordPurpose = Schema.Literal(
  "dkim",
  "mail_from_mx",
  "mail_from_spf",
  "return_path",
);
export type CustomEmailDnsRecordPurpose =
  typeof CustomEmailDnsRecordPurpose.Type;

/** One DNS record a merchant publishes for a custom email sender. `host` is fully qualified. */
export const CustomEmailDnsRecord = Schema.Struct({
  type: CustomEmailDnsRecordType,
  host: Schema.String,
  value: Schema.String,
  purpose: CustomEmailDnsRecordPurpose,
  verified: Schema.Boolean,
});
export type CustomEmailDnsRecord = typeof CustomEmailDnsRecord.Type;

/** Postmark only reports verified or not, so its senders are `verified` or `pending`. */
export const CustomEmailDkimStatus = Schema.Literal(
  "verified",
  "pending",
  "failed",
  "not_started",
);
export type CustomEmailDkimStatus = typeof CustomEmailDkimStatus.Type;

/**
 * The domain a custom email sender sends from: everything after the last `@`,
 * trimmed and lowercased. SES sending requires an `email_domain` whose domain
 * equals this exactly.
 */
export const customEmailSenderDomain = (email: string): string =>
  (email.split("@").pop() ?? "").trim().toLowerCase();

const POSTMARK_DEFAULT_RETURN_PATH_PREFIX = "pm-bounces";

/** Postmark may return a bare prefix (or nothing) for the return path; qualify it with the sender's domain. */
function qualifyHost(host: string, domain: string): string {
  const trimmed = host.trim().replace(/\.$/, "").toLowerCase();
  if (trimmed === domain || trimmed.endsWith(`.${domain}`)) {
    return trimmed;
  }
  return `${trimmed}.${domain}`;
}

/** The DKIM TXT and return-path CNAME a Postmark sender signature needs. */
export function postmarkCustomEmailDnsRecords(input: {
  readonly email: string;
  readonly dkimHost: string;
  readonly dkimText: string;
  readonly returnPathDomain: string;
  readonly returnPathValue: string | null;
  readonly dkimVerified: boolean;
  readonly returnPathVerified: boolean;
}): CustomEmailDnsRecord[] {
  const domain = customEmailSenderDomain(input.email);
  return [
    {
      type: "TXT",
      host: qualifyHost(input.dkimHost, domain),
      value: input.dkimText,
      purpose: "dkim",
      verified: input.dkimVerified,
    },
    {
      type: "CNAME",
      host: qualifyHost(
        input.returnPathDomain || POSTMARK_DEFAULT_RETURN_PATH_PREFIX,
        domain,
      ),
      value: input.returnPathValue ?? "",
      purpose: "return_path",
      verified: input.returnPathVerified,
    },
  ];
}
